import { useEffect, useMemo, useState } from "react";
import {
  Alert, Button, Checkbox, Group, Modal, NumberInput, Paper, ScrollArea, Select,
  Stack, Switch, Tabs, Text, TextInput, Title, Tooltip,
} from "@mantine/core";
import { orderStrategies } from "./featureSettingsUtils";

export type NumericMode = "absolute" | "percentage" | "probability_points";
export type NumericRule = { mode: NumericMode; green: number; yellow: number };
export type CategoricalRule = { mismatch_status: "yellow" | "red" };
export type FeatureAlertRule = {
  enabled: boolean;
  yellow_runner_share_threshold_pct: number;
};
export type StrategyAlertSettings = {
  enabled: boolean;
  rules: {
    fundamental_numeric: Record<string, FeatureAlertRule>;
    fundamental_categorical: Record<string, FeatureAlertRule>;
    market_price: FeatureAlertRule;
    market_volume: FeatureAlertRule;
    historic_market: Record<string, FeatureAlertRule>;
  };
};
export type StrategyFeatureSettings = {
  fundamental_numeric: Record<string, NumericRule>;
  fundamental_categorical: Record<string, CategoricalRule>;
  market_price: NumericRule;
  market_volume: NumericRule;
  historic_market: Record<string, NumericRule>;
  alerts: StrategyAlertSettings;
};
export type FeatureSettingsPayload = {
  schema_version: number;
  strategies: string[];
  catalog: {
    fundamental_numeric: string[];
    fundamental_categorical: string[];
    historic_market: string[];
    market_price_count: number;
    market_volume_count: number;
  };
  models: Record<string, StrategyFeatureSettings>;
};
export type FeatureUpdate = {
  group: Exclude<keyof StrategyFeatureSettings, "alerts">;
  feature?: string;
  rule: NumericRule | CategoricalRule;
  alert: FeatureAlertRule;
  apply_to_all: boolean;
};

const LAST_RACE_BSP = "bsp:LastRaceBSP_win";

function cloneSettings(value: StrategyFeatureSettings): StrategyFeatureSettings {
  return structuredClone(value);
}

function sameRule(left: NumericRule | CategoricalRule, right: NumericRule | CategoricalRule) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function sameAlert(left: FeatureAlertRule, right: FeatureAlertRule) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function alertRule(
  settings: StrategyFeatureSettings,
  group: Exclude<keyof StrategyFeatureSettings, "alerts">,
  feature?: string,
) {
  const groupRules = settings.alerts.rules[group];
  return feature
    ? (groupRules as Record<string, FeatureAlertRule>)[feature]
    : groupRules as FeatureAlertRule;
}

function updateKey(group: string, feature?: string) {
  return `${group}\u0000${feature || ""}`;
}

function changedUpdates(
  saved: StrategyFeatureSettings,
  draft: StrategyFeatureSettings,
  applyAll: Record<string, boolean>,
): FeatureUpdate[] {
  const updates: FeatureUpdate[] = [];
  for (const group of ["fundamental_numeric", "fundamental_categorical", "historic_market"] as const) {
    for (const [feature, rule] of Object.entries(draft[group])) {
      if (!sameRule(saved[group][feature], rule)) {
        updates.push({ group, feature, rule, alert: alertRule(draft, group, feature),
          apply_to_all: !!applyAll[updateKey(group, feature)] });
      } else if (!sameAlert(alertRule(saved, group, feature), alertRule(draft, group, feature))) {
        updates.push({ group, feature, rule, alert: alertRule(draft, group, feature),
          apply_to_all: !!applyAll[updateKey(group, feature)] });
      }
    }
  }
  for (const group of ["market_price", "market_volume"] as const) {
    if (!sameRule(saved[group], draft[group]) ||
        !sameAlert(alertRule(saved, group), alertRule(draft, group))) {
      updates.push({ group, rule: draft[group], alert: alertRule(draft, group),
        apply_to_all: !!applyAll[updateKey(group)] });
    }
  }
  return updates;
}

function validationError(settings: StrategyFeatureSettings): string | null {
  const rules = [
    ...Object.values(settings.fundamental_numeric),
    ...Object.values(settings.historic_market),
    settings.market_price,
    settings.market_volume,
  ];
  for (const rule of rules) {
    if (![rule.green, rule.yellow].every((value) =>
      typeof value === "number" && Number.isFinite(value) && value >= 0)) {
      return "Thresholds must be non-negative numbers";
    }
    if (rule.green > rule.yellow) return "Green threshold cannot exceed yellow threshold";
    if (rule.mode === "probability_points" && rule.yellow > 100) {
      return "Probability-point thresholds cannot exceed 100";
    }
  }
  const alertRules = [
    ...Object.values(settings.alerts.rules.fundamental_numeric),
    ...Object.values(settings.alerts.rules.fundamental_categorical),
    settings.alerts.rules.market_price,
    settings.alerts.rules.market_volume,
    ...Object.values(settings.alerts.rules.historic_market),
  ];
  for (const rule of alertRules) {
    const threshold = rule.yellow_runner_share_threshold_pct;
    if (typeof threshold !== "number" || !Number.isFinite(threshold) || threshold < 0 || threshold > 100) {
      return "Yellow alert thresholds must be between 0 and 100%";
    }
  }
  return null;
}

function modeLabel(mode: NumericMode) {
  if (mode === "probability_points") return "Probability points";
  return mode === "percentage" ? "Percentage" : "Absolute";
}

function NumericRuleRow({
  name, rule, saved, alert, savedAlert, modes, applyAll, onChange, onAlertChange, onApplyAll,
}: {
  name: string;
  rule: NumericRule;
  saved: NumericRule;
  alert: FeatureAlertRule;
  savedAlert: FeatureAlertRule;
  modes: NumericMode[];
  applyAll: boolean;
  onChange: (rule: NumericRule) => void;
  onAlertChange: (rule: FeatureAlertRule) => void;
  onApplyAll: (checked: boolean) => void;
}) {
  const dirty = !sameRule(saved, rule) || !sameAlert(savedAlert, alert);
  const canProduceYellow = rule.yellow > rule.green;
  return <Paper withBorder p="sm">
    <Stack gap="xs">
      <Text fw={600} size="sm" style={{ overflowWrap: "anywhere" }}>{name}</Text>
      <Group grow align="end">
        {modes.length > 1 ? <Select label="Compare as" allowDeselect={false}
          data={modes.map((mode) => ({ value: mode, label: modeLabel(mode) }))}
          value={rule.mode} onChange={(value) => value && onChange({ ...rule, mode: value as NumericMode })} />
          : <TextInput label="Compare as" value={modeLabel(rule.mode)} readOnly />}
        <NumberInput label="Green up to" min={0} value={rule.green}
          onChange={(value) => typeof value === "number" && onChange({ ...rule, green: value })} />
        <NumberInput label="Yellow up to" min={0} value={rule.yellow}
          onChange={(value) => typeof value === "number" && onChange({ ...rule, yellow: value })} />
      </Group>
      <Group grow align="end">
        <Switch label="Alert on this rule" checked={alert.enabled}
          onChange={(event) => onAlertChange({ ...alert, enabled: event.currentTarget.checked })} />
        {canProduceYellow ? <NumberInput label="Yellow alert above (%)" min={0} max={100}
          disabled={!alert.enabled} value={alert.yellow_runner_share_threshold_pct}
          onChange={(value) => typeof value === "number" && onAlertChange({
            ...alert, yellow_runner_share_threshold_pct: value,
          })} /> : <Text size="xs" c="dimmed">
          This rule has no yellow comparison band.
        </Text>}
      </Group>
      {dirty && <Checkbox label="Apply this feature to all models" checked={applyAll}
        onChange={(event) => onApplyAll(event.currentTarget.checked)} />}
    </Stack>
  </Paper>;
}

function CategoricalRuleRow({
  name, rule, saved, alert, savedAlert, applyAll, onChange, onAlertChange, onApplyAll,
}: {
  name: string;
  rule: CategoricalRule;
  saved: CategoricalRule;
  alert: FeatureAlertRule;
  savedAlert: FeatureAlertRule;
  applyAll: boolean;
  onChange: (rule: CategoricalRule) => void;
  onAlertChange: (rule: FeatureAlertRule) => void;
  onApplyAll: (checked: boolean) => void;
}) {
  const dirty = !sameRule(saved, rule) || !sameAlert(savedAlert, alert);
  return <Paper withBorder p="sm">
    <Group justify="space-between" align="end" wrap="wrap">
      <Text fw={600} size="sm" style={{ overflowWrap: "anywhere" }}>{name}</Text>
      <Select label="When values differ" allowDeselect={false} w={180}
        data={[{ value: "yellow", label: "Yellow" }, { value: "red", label: "Red" }]}
        value={rule.mismatch_status}
        onChange={(value) => value && onChange({ mismatch_status: value as "yellow" | "red" })} />
      <Switch label="Alert on this rule" checked={alert.enabled}
        onChange={(event) => onAlertChange({ ...alert, enabled: event.currentTarget.checked })} />
      {rule.mismatch_status === "yellow" && <NumberInput label="Yellow alert above (%)"
        min={0} max={100} w={190} disabled={!alert.enabled}
        value={alert.yellow_runner_share_threshold_pct}
        onChange={(value) => typeof value === "number" && onAlertChange({
          ...alert, yellow_runner_share_threshold_pct: value,
        })} />}
    </Group>
    {dirty && <Checkbox mt="xs" label="Apply this feature to all models" checked={applyAll}
      onChange={(event) => onApplyAll(event.currentTarget.checked)} />}
  </Paper>;
}

export default function FeatureSettingsModal({
  opened, payload, preferredStrategies, onClose, onSave,
}: {
  opened: boolean;
  payload: FeatureSettingsPayload;
  preferredStrategies: string[];
  onClose: () => void;
  onSave: (
    strategy: string, updates: FeatureUpdate[], alertsEnabled: boolean
  ) => Promise<void>;
}) {
  const strategies = useMemo(
    () => orderStrategies(payload.strategies, preferredStrategies),
    [payload.strategies, preferredStrategies],
  );
  const [active, setActive] = useState(strategies[0] || "");
  const [draft, setDraft] = useState<StrategyFeatureSettings | null>(null);
  const [applyAll, setApplyAll] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [discardOpen, setDiscardOpen] = useState(false);

  useEffect(() => {
    if (!strategies.includes(active)) setActive(strategies[0] || "");
  }, [active, strategies]);

  useEffect(() => {
    if (opened && active && payload.models[active]) {
      setDraft(cloneSettings(payload.models[active]));
      setApplyAll({});
    }
  }, [opened, active, payload]);

  if (!draft || !active || !payload.models[active]) return null;
  const saved = payload.models[active];
  const updates = changedUpdates(saved, draft, applyAll);
  const dirty = updates.length > 0 || saved.alerts.enabled !== draft.alerts.enabled;
  const error = validationError(draft);
  const needle = search.trim().toLocaleLowerCase();
  const matches = (value: string) => !needle || value.toLocaleLowerCase().includes(needle);
  const matchingGroup = (label: string) => !!needle && label.toLocaleLowerCase().includes(needle);
  const numeric = payload.catalog.fundamental_numeric.filter((feature) =>
    matchingGroup("Fundamental numeric") || matches(feature));
  const categorical = payload.catalog.fundamental_categorical.filter((feature) =>
    matchingGroup("Fundamental categorical") || matches(feature));
  const historic = payload.catalog.historic_market.filter((feature) =>
    matchingGroup("Historic market") || matches(feature));
  const currentMarketMatches = matchingGroup("Current-race market");
  const showPrice = currentMarketMatches || matches("Betfair current-race Price");
  const showVolume = currentMarketMatches || matches("Betfair current-race Volume");
  const hasResults = numeric.length || categorical.length || historic.length || showPrice || showVolume;

  const changeMappedRule = <Group extends "fundamental_numeric" | "historic_market">(
    group: Group, feature: string, rule: NumericRule,
  ) => setDraft((current) => current && ({
    ...current, [group]: { ...current[group], [feature]: rule },
  }));
  const changeCategorical = (feature: string, rule: CategoricalRule) => setDraft((current) => current && ({
    ...current,
    fundamental_categorical: { ...current.fundamental_categorical, [feature]: rule },
  }));
  const changeAlertRule = (
    group: Exclude<keyof StrategyFeatureSettings, "alerts">,
    feature: string | undefined,
    rule: FeatureAlertRule,
  ) => setDraft((current) => {
    if (!current) return current;
    const currentGroup = current.alerts.rules[group];
    const nextGroup = feature
      ? { ...(currentGroup as Record<string, FeatureAlertRule>), [feature]: rule }
      : rule;
    return {
      ...current,
      alerts: {
        ...current.alerts,
        rules: { ...current.alerts.rules, [group]: nextGroup },
      },
    };
  });
  const setApply = (group: string, feature: string | undefined, checked: boolean) =>
    setApplyAll((current) => ({ ...current, [updateKey(group, feature)]: checked }));
  const requestClose = () => dirty ? setDiscardOpen(true) : onClose();
  const revert = () => {
    setDraft(cloneSettings(saved));
    setApplyAll({});
    setSaveError("");
  };
  const save = async () => {
    if (!dirty || error) return;
    setSaving(true);
    setSaveError("");
    try {
      await onSave(active, updates, draft.alerts.enabled);
      setApplyAll({});
    } catch (reason: unknown) {
      setSaveError(reason instanceof Error ? reason.message : "Unable to save feature settings");
    } finally {
      setSaving(false);
    }
  };

  return <>
    <Modal opened={opened} onClose={requestClose} fullScreen title="Feature settings"
      closeButtonProps={{ "aria-label": "Close modal" }}
      styles={{
        content: { display: "flex", flexDirection: "column", height: "100dvh" },
        body: {
          display: "flex", flex: 1, flexDirection: "column", minHeight: 0,
          overflow: "hidden", padding: 0,
        },
      }}>
      <ScrollArea style={{ flex: 1, minHeight: 0 }} type="auto" offsetScrollbars>
      <Stack gap="md" p="md">
        <Text size="sm" c="dimmed">
          These settings are global. Every authenticated user can update them.
        </Text>
        <ScrollArea type="auto" offsetScrollbars>
          <Tabs value={active} onChange={(value) => value && !dirty && setActive(value)}>
            <Tabs.List style={{ flexWrap: "nowrap" }}>{strategies.map((strategy) =>
              <Tooltip key={strategy} label="Save changes first" disabled={!dirty || strategy === active}>
                <span><Tabs.Tab value={strategy} disabled={dirty && strategy !== active}>
                  {strategy}
                </Tabs.Tab></span>
              </Tooltip>)}</Tabs.List>
          </Tabs>
        </ScrollArea>
        <Paper withBorder p="sm">
          <Group justify="space-between" align="center">
            <Stack gap={0}>
              <Text fw={600}>Strategy audit alerts</Text>
              <Text size="xs" c="dimmed">
                Send alerts for enabled rules after eligible audit runs.
              </Text>
            </Stack>
            <Switch aria-label="Strategy audit alerts" checked={draft.alerts.enabled}
              onChange={(event) => setDraft((current) => current && ({
                ...current,
                alerts: { ...current.alerts, enabled: event.currentTarget.checked },
              }))} />
          </Group>
        </Paper>
        <TextInput label="Find a feature" placeholder="Search by feature name" value={search}
          onChange={(event) => setSearch(event.currentTarget.value)} />

        {!!numeric.length && <Stack gap="xs"><Title order={4}>Fundamental numeric</Title>
          {numeric.map((feature) => <NumericRuleRow key={feature} name={feature}
            rule={draft.fundamental_numeric[feature]} saved={saved.fundamental_numeric[feature]}
            alert={draft.alerts.rules.fundamental_numeric[feature]}
            savedAlert={saved.alerts.rules.fundamental_numeric[feature]}
            modes={["absolute", "percentage"]}
            applyAll={!!applyAll[updateKey("fundamental_numeric", feature)]}
            onChange={(rule) => changeMappedRule("fundamental_numeric", feature, rule)}
            onAlertChange={(rule) => changeAlertRule("fundamental_numeric", feature, rule)}
            onApplyAll={(checked) => setApply("fundamental_numeric", feature, checked)} />)}
        </Stack>}

        {!!categorical.length && <Stack gap="xs"><Title order={4}>Fundamental categorical</Title>
          {categorical.map((feature) => <CategoricalRuleRow key={feature} name={feature}
            rule={draft.fundamental_categorical[feature]}
            saved={saved.fundamental_categorical[feature]}
            alert={draft.alerts.rules.fundamental_categorical[feature]}
            savedAlert={saved.alerts.rules.fundamental_categorical[feature]}
            applyAll={!!applyAll[updateKey("fundamental_categorical", feature)]}
            onChange={(rule) => changeCategorical(feature, rule)}
            onAlertChange={(rule) => changeAlertRule("fundamental_categorical", feature, rule)}
            onApplyAll={(checked) => setApply("fundamental_categorical", feature, checked)} />)}
        </Stack>}

        {(showPrice || showVolume) && <Stack gap="xs"><Title order={4}>Current-race market</Title>
          {showPrice && <NumericRuleRow name={`Price · ${payload.catalog.market_price_count} features`}
            rule={draft.market_price} saved={saved.market_price} modes={["probability_points"]}
            alert={draft.alerts.rules.market_price} savedAlert={saved.alerts.rules.market_price}
            applyAll={!!applyAll[updateKey("market_price")]}
            onChange={(rule) => setDraft((current) => current && ({ ...current, market_price: rule }))}
            onAlertChange={(rule) => changeAlertRule("market_price", undefined, rule)}
            onApplyAll={(checked) => setApply("market_price", undefined, checked)} />}
          {showVolume && <NumericRuleRow name={`Volume · ${payload.catalog.market_volume_count} features`}
            rule={draft.market_volume} saved={saved.market_volume} modes={["absolute"]}
            alert={draft.alerts.rules.market_volume} savedAlert={saved.alerts.rules.market_volume}
            applyAll={!!applyAll[updateKey("market_volume")]}
            onChange={(rule) => setDraft((current) => current && ({ ...current, market_volume: rule }))}
            onAlertChange={(rule) => changeAlertRule("market_volume", undefined, rule)}
            onApplyAll={(checked) => setApply("market_volume", undefined, checked)} />}
        </Stack>}

        {!!historic.length && <Stack gap="xs"><Title order={4}>Historic market</Title>
          {historic.map((feature) => <NumericRuleRow key={feature} name={feature}
            rule={draft.historic_market[feature]} saved={saved.historic_market[feature]}
            alert={draft.alerts.rules.historic_market[feature]}
            savedAlert={saved.alerts.rules.historic_market[feature]}
            modes={feature === LAST_RACE_BSP
              ? ["absolute", "percentage", "probability_points"] : ["absolute", "percentage"]}
            applyAll={!!applyAll[updateKey("historic_market", feature)]}
            onChange={(rule) => changeMappedRule("historic_market", feature, rule)}
            onAlertChange={(rule) => changeAlertRule("historic_market", feature, rule)}
            onApplyAll={(checked) => setApply("historic_market", feature, checked)} />)}
        </Stack>}

        {!hasResults && <Alert color="gray">No features match “{search}”.</Alert>}
        {error && <Alert color="red">{error}</Alert>}
        {saveError && <Alert color="red">{saveError}</Alert>}
      </Stack>
      </ScrollArea>
      <Paper data-testid="feature-settings-footer" withBorder p="sm" radius={0}
        style={{ flexShrink: 0, zIndex: 10 }}>
        <Group justify="space-between">
          <Text size="sm" c={dirty ? "orange" : "dimmed"}>{dirty ? "Unsaved changes" : "Saved"}</Text>
          <Group><Button variant="light" onClick={revert} disabled={!dirty || saving}>Revert</Button>
            <Button onClick={save} disabled={!dirty || !!error} loading={saving}>Save</Button></Group>
        </Group>
      </Paper>
    </Modal>
    <Modal opened={discardOpen} onClose={() => setDiscardOpen(false)} title="Discard changes?" centered>
      <Stack><Text>Your unsaved feature settings will be lost.</Text>
        <Group justify="flex-end"><Button variant="light" onClick={() => setDiscardOpen(false)}>Keep editing</Button>
          <Button color="red" onClick={() => { setDiscardOpen(false); revert(); onClose(); }}>
            Discard changes
          </Button></Group>
      </Stack>
    </Modal>
  </>;
}
